import { cue, unlock } from '../core/audio'
import { clear, h, isAbort, wait } from '../core/dom'
import { haptic } from '../core/haptics'
import { back } from '../core/router'
import { markRitualDone } from '../core/store'
import { speak, shutUp } from '../core/voice'
import {
  BREATH_PATTERNS,
  phaseSequence,
  type BreathPattern,
  type Phase,
} from '../data/breathPatterns'
import { getLang, num, t } from '../i18n'
import { topbar } from './shared'

const SMALL = 1
const LARGE = 2.35

export function renderBreath(root: HTMLElement, signal: AbortSignal): void {
  // Speech outlives its own screen unless something stops it explicitly.
  signal.addEventListener('abort', shutUp, { once: true })

  pickPattern(root, signal)
}

function pickPattern(root: HTMLElement, signal: AbortSignal): void {
  clear(root)
  const d = t()
  const lang = getLang()

  root.append(
    topbar(d.breath.title),
    h(
      'div',
      { class: 'stack fade-in' },
      h('p', { class: 'section-label' }, d.breath.pick),
      ...BREATH_PATTERNS.map((p) =>
        h(
          'button',
          {
            class: 'tile',
            onclick: () => {
              haptic.tap()
              void run(root, signal, p)
            },
          },
          h(
            'span',
            { class: 'grow' },
            h('span', { class: 'label', style: { display: 'block' } }, p.label[lang]),
            h('span', { class: 'sub', style: { display: 'block' } }, p.note[lang]),
          ),
          h('span', { class: 'glyph faint' }, rhythmGlyph(p)),
        ),
      ),
    ),
  )
}

/** A compact "4·7·8" style summary so patterns are comparable at a glance. */
function rhythmGlyph(p: BreathPattern): string {
  return phaseSequence(p)
    .map((s) => num(s.seconds))
    .join('·')
}

async function run(root: HTMLElement, signal: AbortSignal, pattern: BreathPattern): Promise<void> {
  await unlock()
  clear(root)
  const d = t()

  const orb = h('div', { class: 'orb', style: { transform: `scale(${SMALL})` } })
  const phaseLabel = h('div', { class: 'breath-phase' })
  const countLabel = h('div', { class: 'breath-count' })
  const roundLabel = h('div', { class: 'breath-round' })

  root.append(
    topbar(pattern.label[getLang()]),
    h(
      'div',
      { class: 'breath-stage fade-in' },
      h(
        'div',
        { class: 'orb-wrap' },
        h('div', { class: 'orb-ring' }),
        h('div', { class: 'orb-ring inner' }),
        orb,
      ),
      h('div', { class: 'stack-sm center' }, phaseLabel, countLabel, roundLabel),
    ),
    h(
      'button',
      {
        class: 'btn ghost block',
        onclick: () => {
          haptic.tap()
          back()
        },
      },
      d.common.stop,
    ),
  )

  const phaseText: Record<Phase, string> = {
    inhale: d.breath.inhale,
    hold: d.breath.hold,
    exhale: d.breath.exhale,
    holdOut: d.breath.holdOut,
  }

  const sequence = phaseSequence(pattern)

  try {
    for (let round = 1; round <= pattern.rounds; round++) {
      roundLabel.textContent = d.breath.rounds(round)

      for (const { phase, seconds } of sequence) {
        phaseLabel.textContent = phaseText[phase]

        // The orb animation duration is derived from the phase length rather
        // than hard-coded, so what you see and what you are told stay in sync
        // no matter which pattern is running.
        if (phase === 'inhale' || phase === 'exhale') {
          orb.style.transitionDuration = `${seconds}s`
          orb.style.transform = `scale(${phase === 'inhale' ? LARGE : SMALL})`
        }

        if (phase === 'inhale') {
          haptic.inhale()
          void cue(392, 0.055)
        } else if (phase === 'exhale') {
          haptic.exhale()
          void cue(294, 0.055)
        } else {
          haptic.hold()
        }

        // Only the two active phases are spoken. Announcing every hold turns a
        // breathing exercise into a stream of narration.
        if (phase === 'inhale' || phase === 'exhale') {
          void speak(phaseText[phase], { rate: 0.72 })
        }

        for (let remaining = seconds; remaining > 0; remaining--) {
          countLabel.textContent = num(remaining)
          await wait(1000, signal)
        }
      }
    }

    finish(root, signal, pattern)
  } catch (err) {
    if (!isAbort(err)) throw err
  }
}

function finish(root: HTMLElement, signal: AbortSignal, pattern: BreathPattern): void {
  markRitualDone('breath')
  haptic.complete()
  const d = t()

  clear(root)
  root.append(
    topbar(),
    h(
      'div',
      { class: 'breath-stage fade-in' },
      h('div', { style: { fontSize: '2.8rem' } }, '🌙'),
      h(
        'div',
        { class: 'stack-sm center' },
        h('h1', {}, d.breath.finishedTitle),
        h('p', { class: 'lede' }, d.breath.finishedBody),
      ),
    ),
    h(
      'div',
      { class: 'stack-sm' },
      h(
        'button',
        {
          class: 'btn primary block',
          onclick: () => {
            haptic.tap()
            void run(root, signal, pattern)
          },
        },
        d.common.start,
      ),
      h(
        'button',
        {
          class: 'btn ghost block',
          onclick: () => {
            haptic.tap()
            back()
          },
        },
        d.common.done,
      ),
    ),
  )
}
